/**
 * 画像のビフォーアフターを比較表示するWebコンポーネント
 * 2つの画像のサイズは揃えること
 * 
 * Data attributes:
 * - data-img-before-src: URL of the "before" image.
 * - data-img-after-src: URL of the "after" image.
 * - data-size: Maximum width of the component in pixels.
 * 
 * Example usage:
 * <script src="./beforeafter.js" type="module" defer></script>
 * <compare-img-before-after
 *   data-img-before-src="before.jpg"
 *   data-img-after-src="after.jpg"
 *   data-size="600">
 * </compare-img-before-after>
 */
class CompareImgBeforeAfter extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        const shadow = this.attachShadow({ mode: 'closed' });
        const style = this.createStyle(this);
        const div = this.createElements(this);
        this.setEvent(div);
        shadow.appendChild(style);
        shadow.appendChild(div);
    }

    createElements(elem) {
        const container = document.createElement('div');
        container.classList.add('image-compare');
        const beforeSrc = elem.dataset.imgBeforeSrc || '';
        const afterSrc = elem.dataset.imgAfterSrc || '';

        container.innerHTML = `
        <div class="image-compare__inner">
            <img class="image-compare__before"
                src="${beforeSrc}" alt="Before"
                loading="lazy">
            <span class="image-compare__after-outer">
                <img class="image-compare__after"
                    src="${afterSrc}" alt="After"
                    loading="lazy">
            </span>
            <label class="image-compare__label">
                <input type="range" min="0" max="100" value="50"
                    class="image-compare__input" aria-label="BeforeとAfterを比較する">
            </label>
        </div>
        `;

        return container;
    }

    setEvent(elem) {
        const clippedImage = elem.querySelector('.image-compare__after');
        const clippingSlider = elem.querySelector('.image-compare__input');

        clippedImage.style.setProperty('--exposure', `${clippingSlider.value}%`);

        clippingSlider.addEventListener('input', (event) => {
            const newValue = `${event.target.value}%`;
            clippedImage.style.setProperty('--exposure', newValue);
        });
    }

    createStyle(elem) {
        const style = document.createElement('style');
        const size = elem.dataset.size || 600;
        style.textContent = `
        .image-compare {
            max-width: ${size}px;
            overflow: hidden;
            margin: 0 auto;
        }
        .image-compare__inner {
            position: relative
        }
        .image-compare__after-outer {
            display: block;
            position: absolute;
            top: 0;
            width: 100%;
            filter: drop-shadow(-4px 0 0 #ffffff)
        }
        .image-compare__before {
            width: 100%
        }
        .image-compare__after {
            --exposure: 50%;
            width: 100%;
            display: block;
            clip-path: polygon(calc(var(--exposure) + 1px) 0, 100% 0, 100% 100%, calc(var(--exposure) + 1px) 100%)
        }
        .image-compare__label {
            align-items: stretch;
            display: flex;
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0
        }
        .image-compare__input {
            --thumb-size: clamp(3em, 10vmin, 5em);
            margin: 0 calc(var(--thumb-size)/-2);
            width: calc(100% + var(--thumb-size));
            cursor: col-resize;
            appearance: none;
            -webkit-appearance: none;
            background: none;
            border: none
        }
        .image-compare__input::-moz-range-thumb {
            --thumb-size: clamp(3em, 10vmin, 5em);
            background-color: rgba(255, 255, 255, 0);
            background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iX+ODrOOCpOODpOODvF8yIiBkYXRhLW5hbWU9IuODrOOCpOODpOODvCAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1NC44NiAzMC44NiI+CiAgPGRlZnM+CiAgICA8c3R5bGU+CiAgICAgIC5jbHMtMSB7CiAgICAgICAgZmlsbDogI2ZmZjsKICAgICAgICBzdHJva2Utd2lkdGg6IDBweDsKICAgICAgfQogICAgPC9zdHlsZT4KICA8L2RlZnM+CiAgPGcgaWQ9Il/jg6zjgqTjgqLjgqbjg4giIGRhdGEtbmFtZT0i44Os44Kk44Ki44Km44OIIj4KICAgIDxnPgogICAgICA8cG9seWdvbiBjbGFzcz0iY2xzLTEiIHBvaW50cz0iMTUuNDMgMzAuODYgMCAxNS40MyAxNS40MyAwIDE1LjQzIDMwLjg2Ii8+CiAgICAgIDxwb2x5Z29uIGNsYXNzPSJjbHMtMSIgcG9pbnRzPSIzOS40MyAwIDU0Ljg2IDE1LjQzIDM5LjQzIDMwLjg2IDM5LjQzIDAiLz4KICAgIDwvZz4KICA8L2c+Cjwvc3ZnPg==);
            background-size: 90%;
            background-position: 1px center;
            background-repeat: no-repeat;
            color: #141414;
            border: 0;
            width: var(--thumb-size);
            height: var(--thumb-size)
        }
        .image-compare__input::-webkit-slider-thumb {
            --thumb-size: clamp(3em, 10vmin, 5em);
            -webkit-appearance: none;
            background-color: rgba(255, 255, 255, 0);
            background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iX+ODrOOCpOODpOODvF8yIiBkYXRhLW5hbWU9IuODrOOCpOODpOODvCAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1NC44NiAzMC44NiI+CiAgPGRlZnM+CiAgICA8c3R5bGU+CiAgICAgIC5jbHMtMSB7CiAgICAgICAgZmlsbDogI2ZmZjsKICAgICAgICBzdHJva2Utd2lkdGg6IDBweDsKICAgICAgfQogICAgPC9zdHlsZT4KICA8L2RlZnM+CiAgPGcgaWQ9Il/jg6zjgqTjgqLjgqbjg4giIGRhdGEtbmFtZT0i44Os44Kk44Ki44Km44OIIj4KICAgIDxnPgogICAgICA8cG9seWdvbiBjbGFzcz0iY2xzLTEiIHBvaW50cz0iMTUuNDMgMzAuODYgMCAxNS40MyAxNS40MyAwIDE1LjQzIDMwLjg2Ii8+CiAgICAgIDxwb2x5Z29uIGNsYXNzPSJjbHMtMSIgcG9pbnRzPSIzOS40MyAwIDU0Ljg2IDE1LjQzIDM5LjQzIDMwLjg2IDM5LjQzIDAiLz4KICAgIDwvZz4KICA8L2c+Cjwvc3ZnPg==);
            background-size: 90%;
            background-position: 1px center;
            background-repeat: no-repeat;
            color: #141414;
            width: var(--thumb-size);
            height: var(--thumb-size)
        }
        `;

        return style;
    }
}

customElements.define( 'compare-img-before-after', CompareImgBeforeAfter );